import type { EditorVersionLinkProps } from 'Shared/editor/editor_version_link';
import type { FlagDefinition } from 'Shared/editor/flags_field';
import type { LanguageSectionProps } from 'Shared/editor/language_section';
import type { WikitextSource } from 'Shared/editor/use_wikitext_editor';
import type { TitleAutogen } from './guide_title';

/** Props bootstrapped by GuideEditLib::displayV2(). */
export type GuideEditV2Props = {
   guideid: number;
   langid: string;
   title: string;
   editorVersionLink: EditorVersionLinkProps | null;
   /** The public view URL of the guide, for post-save navigation. */
   viewUrl: string;
   /**
    * The revision-history pages, replacing the History links the v1
    * editors carried in their nav (#64082). stepUrlBase is a prefix the
    * client appends a stepid to, since the open step lives in the URL
    * hash. Null when the viewer can't see history
    * (GuidePrivilege::canIViewGuideHistory).
    */
   history: {
      guideUrl: string;
      introUrl: string;
      stepUrlBase: string;
   } | null;
   /**
    * GuideTranslation::hasDeletePrivilege — whether this user may delete the
    * whole guide (admin/moderator, or the sole contributor with no release).
    * Gates the Details tab's delete action; the endpoint re-enforces it.
    */
   canDelete: boolean;
   /**
    * Where to send the user after a successful delete: the guide's device
    * page (GuideURI::viewDevice). Resolved server-side while the guide still
    * exists, since it reads guide_meta the soft delete leaves in place.
    */
   deleteRedirectUrl: string;
   initialTab: EditTab;
   initialStepid: number | null;
   /** Insert position from a new-after step URL; null everywhere else. */
   initialNewStepOrderby: number | null;
   /**
    * The guide's video, for video guides: the timestamp token flavor the
    * steps use plus what the step timestamp editor's preview player needs.
    * Null hides the step timestamp editor entirely.
    */
   video: GuideVideoInfo | null;
   /**
    * GuidePrivilege::canISetVideoGuideMode — whether the Video field offers
    * the introduction-video / video-guide mode toggle (admins and
    * moderators). Others see the radios disabled on an existing video
    * guide, and not at all otherwise.
    */
   canSetVideoGuideMode: boolean;
   /**
    * GuidePrivilege::canIEditGuideMetadata — whether the Metadata tab (patrol
    * threshold, tags, private-guide allowlists) is shown at all. That tab is a
    * moderation surface, so it's admin + moderator only; regular editors set
    * flags from the Details tab instead.
    */
   canEditMetadata: boolean;
   /** The auto-title templates and gate; see guide_title.ts. */
   titleAutogen: TitleAutogen;
   /** Server-parsed by the GuideIntro parser, mounted in useWikitextEditor. */
   introduction: WikitextSource;
   conclusion: WikitextSource;
   /** Discrete slider stops for the time estimate, seconds ascending. */
   timeIncrements: TimeIncrement[];
   /**
    * The unfiltered prerequisite list (DB truth).
    * The API's list drops prereqs the viewer can't see; editing must not.
    */
   prerequisites: BootPrerequisite[];
   metadata: GuideEditMetadataProps;
   /**
    * Whether to show the Creative Commons contributor-licensing notice
    * (public-editing sites licensed under Creative Commons), matching v1's
    * disclaimer.phtml.
    */
   showLicenseDisclaimer: boolean;
   /**
    * The guide-type selector's seed. Null when the site
    * organizes guides by a single type, in which case no selector shows.
    * `type` isn't on the guide GET, so the current value is bootstrapped.
    */
   guideType: GuideTypeProps | null;
   /** Difficulty names with their short descriptions, for the picker hint. */
   difficulties: DifficultyOption[];
};

export type GuideVideoInfo = {
   type: 'youtube' | 'ifixit';
   /** YouTube video id, or the hosted video's videoid. */
   videoId: string;
   /** Hosted-video encodings for the preview player; null for YouTube. */
   videoUrls: {
      mp4_720?: string;
      mp4_592?: string;
      webm?: string;
      ogg?: string;
   } | null;
};

export type GuideTypeProps = {
   current: string;
   options: {
      value: string;
      label: string;
      /** What the type is for, shown under the selector; null for custom
       *  site types that don't have one. */
      description: string | null;
      /** Whether this type has a part/thing (a non-empty config prompt). */
      thingRequired: boolean;
      /** The subject field's label ("What are you disassembling?"); null for
       *  types without a thing (teardown, technique), which hide the field. */
      thingPrompt: string | null;
   }[];
};

export type DifficultyOption = {
   /** Canonical English difficulty — the only form PATCH accepts. */
   value: string;
   /** Localized display name. */
   name: string;
   description: string;
};

export type TimeIncrement = {
   seconds: number;
   label: string;
};

export type BootPrerequisite = {
   guideid: number;
   /** Null when the current user can't view the prereq guide. */
   title: string | null;
   /** Prereq guide's thumbnail; null when it has no image or isn't visible. */
   imageUrl: string | null;
};

type EditTab = 'intro' | 'details' | 'steps' | 'metadata';

/**
 * Which Metadata tab sections the current user may edit, with their seed
 * data. A null section means the user lacks the privilege and the section
 * isn't rendered.
 */
export type GuideEditMetadataProps = {
   /** GuidePrivilege::canIChangeGuidePrivacy — gates privacy, teams, users. */
   canChangePrivacy: boolean;
   /** GuidePrivilege::canIEditGuideAuthor — the Author section (admins). */
   canChangeAuthor: boolean;
   patrol: {
      threshold: number;
      maxAllowed: number;
      maxDisplayed: number;
      /** PatrolLib::THRESHOLD_LEVELS with cached approver counts. */
      levels: Array<{ threshold: number; users: number | null }>;
      helpUrl: string;
   } | null;
   tags: {
      /** Full typed list; saveTags removes anything missing from it. */
      tags: string[];
      maxLength: number;
   } | null;
   flags: {
      /** Flags this editor may add or remove. */
      available: GuideFlagDefinition[];
      /** Full metadata for existing flags, including privileged flags. */
      current: GuideFlagDefinition[];
   } | null;
   /**
    * GuidePrivilege::canIEditAnyGuidesLanguage, and only the languages the
    * guide doesn't already have. Null hides the section.
    */
   language: LanguageSectionProps | null;
};

export type GuideFlagDefinition = FlagDefinition;

/** Image info as built by GuideAPILib_2_0::buildImageInfo(). */
type ApiImage = {
   id: number;
   guid: string;
   original: string;
   mini?: string;
   thumbnail?: string;
   standard?: string;
   medium?: string;
   large?: string;
};

/**
 * The guide's author from GET /guides/{id}. A user without an avatar still
 * carries an image object: the API substitutes the user's default avatar
 * with a sentinel id (GuideAPILib_2_0::DEFAULT_AVATAR_API_IMAGEID) and real
 * rendition URLs — so `image.mini` is always displayable, but the id must
 * never be treated as a real imageid.
 */
type ApiAuthor = {
   userid: number;
   username: string;
   unique_username: string | null;
   image: ApiImage | null;
};

/** One step line from GET /guides/{id}; text_raw is the editable wikitext. */
type ApiLine = {
   lineid: number | null;
   text_raw: string;
   /** Server-rendered HTML from the GuideStep wikitext parser. */
   text_rendered: string;
   bullet: string;
   level: number;
};

export type ApiStepMedia =
   | { type: 'image'; data: ApiImage[] }
   | { type: 'video'; data: { objectid: number; [key: string]: unknown } }
   | { type: 'embed'; data: { objectid: number; [key: string]: unknown } };

export type ApiStep = {
   stepid: number;
   orderby: number;
   /** Composite concurrency token; PATCH/DELETE require it as ?revisionid=. */
   revisionid: string;
   title: string;
   lines: ApiLine[];
   media: ApiStepMedia;
};

/**
 * A tool or part. GET returns the name as `text`; PATCH expects it back as
 * `name` (see GuidesApi::$itemFields).
 */
export type ApiGuideItem = {
   text: string;
   notes: string | null;
   type: string | null;
   quantity: number;
   url: string;
   /**
    * The item wiki page. Always present: saving a guide creates the page for
    * any item that lacks one.
    */
   wiki_url: string;
   /**
    * That page's title, which `text` (its display title) can differ from.
    * PATCH resolves an item by title, so a save has to send this one back.
    */
   wiki_title: string;
   /** The API serializes this as 0/1. */
   isoptional: boolean | 0 | 1;
   /** Whether the item is featured on the guide. Serialized as 0/1. */
   featured: boolean | 0 | 1;
   /** Catalog item thumbnail; null when the item page has no image. */
   thumbnail: string | null;
};

type ApiDocument = {
   documentid: number;
   /** Title or filename. */
   text: string;
   url: string;
   extension: string;
   /**
    * The document_file_information classification — the same field the wiki
    * editor reads via DisplayDocument::getDocumentType(). Empty for types
    * with no distinct icon.
    */
   document_type: string;
};

type ApiPrerequisite = {
   guideid: number;
   title: string;
   /** Blurb image with rendition URLs; null when the guide has no image. */
   image?: { mini?: string; thumbnail?: string; standard?: string; medium?: string } | null;
};

export type ApiGuide = {
   guideid: number;
   /** Concurrency token for guide-level PATCH and steporder PUT. */
   revisionid: string;
   /** The primary guide photo (guide_meta.imageid); null when unset. */
   image: ApiImage | null;
   /** Always present on the GET; optional so test fixtures stay light. */
   author?: ApiAuthor;
   title: string;
   category: string;
   subject: string;
   summary: string;
   introduction_raw: string;
   conclusion_raw: string;
   difficulty: string;
   time_required_min: number | null;
   time_required_max: number | null;
   tools: ApiGuideItem[];
   parts: ApiGuideItem[];
   documents: ApiDocument[];
   prerequisites: ApiPrerequisite[];
   flags: { flagid: string; title: string }[];
   steps: ApiStep[];
   public: boolean | number;
};

/**
 * The canonical difficulty values GuidesApi::$guideDifficulties accepts.
 * GET localizes `difficulty`, so on non-English guides the stored value may
 * not match these; the select adds the stored value as an extra option
 * rather than silently rewriting it.
 */
export const DIFFICULTIES = [
   'Very easy',
   'Easy',
   'Moderate',
   'Difficult',
   'Very difficult',
] as const;
